import { Radio } from '@fountain/fountain-ui-components';
import {
  DragAndDropOutlineIcon,
  Icon,
  PlusOutlineIcon,
  XOutlineIcon,
} from '@fountain/ripple/icon';
import {
  Button,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { ApprovalsService, CancelablePromise } from 'api-clients/monolith';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { FormattedMessage, useIntl } from 'react-intl';

import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import { BackupApprovers } from './BackupApprovers';
import { ConditionBuilder } from './ConditionBuilder';
import { CatalogState } from './conditionForm';
import { focusAfterRemoval } from './focusAfterRemoval';
import messages from './messages';
import {
  ApproverLogic,
  approverCount,
  draftFor,
  draftForNew,
  emptyLevel,
  hasErrors,
  hiddenScopeConditions,
  levelHasError,
  LevelDraft,
  reorderLevels,
  RuleDraft,
  RulePayload,
  SCOPE_CONDITION_PREFIX,
  scopeDropsAlways,
  toPayload,
  validate,
  ValidationErrors,
  withScopeChoice,
} from './ruleForm';
import {
  SearchableMultiSelect,
  SearchableMultiSelectOption,
} from './SearchableMultiSelect';
import { SegmentedChoice } from './SegmentedChoice';
import { useStyles } from './styles';
import { ApprovalRule, OFFER, SubjectType, UserOption } from './types';

/** What a successful create returns; update returns the same rule shape. */
type SaveRuleResponse = Awaited<
  ReturnType<typeof ApprovalsService.postInternalApiApprovalsRules>
>;

export interface RuleEditorFormProps {
  /** The rule being edited; absent means this is a new rule. */
  rule?: ApprovalRule;
  /** Only used when creating — an existing rule's type is fixed. */
  subjectType?: SubjectType;
  userOptions: UserOption[];
  /**
   * The typed field catalog the condition builder is driven by (AXHE-3929),
   * carried as a state rather than an array so the builder can tell a failed
   * fetch from an account with nothing to gate on.
   */
  catalog: CatalogState;
  /** Retries the catalog fetch, offered when it failed. */
  onRetryCatalog: () => void;
  /**
   * The 1-based level card to scroll to on open, set when the panel was opened
   * from a level row rather than from the rule (AXHE-4464). Absent opens at the
   * top, which is where a rule is edited from.
   */
  focusLevel?: number;
  onClose: () => void;
  onSaved: () => void;
}

export interface RuleEditorPanelProps extends RuleEditorFormProps {
  open: boolean;
}

/**
 * The scope radio group's anchors.
 *
 * Module constants rather than derived per render: a `<fieldset>` takes its
 * accessible name from its `<legend>`, and the group's description is whichever
 * of the notes under it is showing — both referenced by id, and the panel is a
 * singleton so the ids cannot collide with a second instance. The
 * `-helper-text` suffix is the shape MUI derives for a `TextField`, so one
 * convention covers every control in the panel.
 */
const SCOPE_LEGEND_ID = 'approval-rule-scope-label';
const SCOPE_HELPER_ID = 'approval-rule-scope-helper-text';
const SCOPE_HIDDEN_ID = 'approval-rule-scope-hidden-conditions';
const SCOPE_ALWAYS_DROP_ID = 'approval-rule-scope-always-dropped';

/**
 * Create and edit an approval rule.
 *
 * A right-hand slide-in rather than a centred modal: this is significant data
 * entry — a name and a variable number of levels each with their own approvers —
 * and a modal is the wrong container for that. It also lets the list stay
 * visible behind the panel, which is what makes "another rule already covers
 * this" noticeable while you are writing one.
 *
 * ## What this panel deliberately cannot switch on or off
 *
 * Neither the rule's `enabled` nor a level's (AXHE-4470). Design Requirements §2
 * locks "only whole rules have an enable/disable toggle. Individual levels
 * cannot be toggled on/off", and the mock puts the rule's one toggle in the
 * list row's Enabled column — not in here. Two switches shipped anyway during
 * the original transcription (AXHE-3945): a per-level "Level active" and a
 * second "Rule active" duplicating the row's.
 *
 * Both values are still *carried* — see `toPayload`. Dropping them would not be
 * a UI-only change: `SaveRule#create_level` defaults an absent `enabled` to
 * true, so a payload that stopped naming it would silently re-activate a level
 * an admin had deliberately switched off, and `RuleMatcher` would start routing
 * approvals through an approver they meant to bypass. So an already-off level
 * keeps its state and says so instead, since nothing can switch it back.
 *
 * ## What it edits, and what it only carries
 *
 * Data-key conditions are edited here via `ConditionBuilder` (AXHE-3929) — both
 * a level's and, since AXHE-4467, the rule's own scope, which the "Apply this
 * rule to" choice below sits on. Every other condition type is carried back
 * untouched; see `toPayload`, where getting this wrong would clear them.
 *
 * A level's backup approvers and the wait that brings them in are edited too
 * (AXHE-3932) — see `BackupApprovers`, which states what it deliberately does not
 * offer. Everything else on a level travels on `LevelDraft#passthrough`.
 *
 * That split is why the "All …" choice can be withheld. Choosing it means "drop
 * every condition scoping this rule", and a panel that cannot render a condition
 * must not offer to delete it — see `hiddenScopeConditions`.
 */
export const RuleEditorForm: React.FC<RuleEditorFormProps> = ({
  rule,
  subjectType = OFFER,
  userOptions,
  catalog,
  onRetryCatalog,
  focusLevel,
  onClose,
  onSaved,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const [draft, setDraft] = useState<RuleDraft>(() =>
    rule ? draftFor(rule) : draftForNew(subjectType),
  );
  // Validation runs on submit, not on keystroke — flagging a name as missing
  // while the user is still walking toward the field is premature.
  const [submitted, setSubmitted] = useState(false);

  // Undefined until the catalog resolves. Both `validate` and `toPayload` treat
  // that as "conditions were not editable this session", which is exactly true:
  // the builder renders its own loading and error states instead of a picker.
  const fieldCatalog = catalog.status === 'ready' ? catalog.catalog : undefined;

  /**
   * The account's people, as the roster picker takes them.
   *
   * Built once and handed to every roster on the panel — each level's own and
   * each backup block's — rather than per card. react-select takes a `setState`
   * whenever `options` changes by identity, and this panel re-renders on every
   * keystroke in the name field.
   *
   * A user with no name falls back to the id it is stored under, which is a
   * worse label than a person's name and still says which person.
   */
  const approverOptions = useMemo(
    (): SearchableMultiSelectOption[] =>
      userOptions.map(user => ({
        value: user.external_id,
        label: user.name ?? user.external_id,
      })),
    [userOptions],
  );

  /**
   * Mints the key for each level added here.
   *
   * A counter that only ever goes up, not `levels.length`: length is not
   * monotonic, so add → remove → add re-issued a key that was already on a card
   * (add to a one-level draft gives `level-new-1`, removing the other card puts
   * the length back to 1, and the next add gives `level-new-1` again).
   *
   * That matters more than a duplicate React key. Every mutation in this panel
   * routes on `level.key` — `updateLevel` patches *every* level whose key
   * matches and remove filters *every* level whose key matches — so two cards
   * sharing a key stop behaving as separate levels while `toPayload` still
   * submits both, and a rule can be saved whose second level holds approvers
   * the admin only entered once.
   *
   * A ref rather than state: it must survive a render without causing one, and
   * nothing renders from it. The `level-new-` prefix keeps these clear of the
   * `level-N` keys `draftFor` mints for persisted levels, so the counter only
   * has to be unique against itself and can start at zero.
   */
  const nextLevelKey = useRef(0);
  const mintLevelKey = () => {
    const key = `level-new-${nextLevelKey.current}`;
    nextLevelKey.current += 1;
    return key;
  };

  /**
   * Brings the level the admin clicked into view (AXHE-4464).
   *
   * On mount only, and deliberately not on every `focusLevel` change: the panel
   * is keyed by rule id in the container, so a different rule remounts it, and
   * within one open the admin is reading and reordering cards — scrolling them
   * out from under a drag would be the panel fighting the user. The dependency
   * list is empty for that reason rather than by omission.
   *
   * `scrollIntoView` is guarded because jsdom does not implement it, and a
   * layout convenience must not be the thing that makes the editor unmountable
   * in a test.
   */
  const focusedLevelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    focusedLevelRef.current?.scrollIntoView?.({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Where a refused save is reported, top to bottom.
   *
   * The footer is pinned, so Save is reachable from anywhere in a long rule —
   * which means the control it was refused for can be off screen when the
   * message appears. Scrolling to it is what keeps the refusal legible; before
   * the footer was pinned, reaching Save meant already being at the bottom.
   *
   * The level cards are held by key rather than by index so a reorder carries
   * each entry with its own card. Keys of removed levels are left behind, and
   * are simply never looked up: `validate` only reports levels the draft still
   * holds.
   */
  const nameFieldRef = useRef<HTMLDivElement | null>(null);
  const scopeSectionRef = useRef<HTMLDivElement | null>(null);
  const levelRefs = useRef<Record<string, HTMLElement | null>>({});
  // Where a removed level's own card sends focus once no other card is left
  // to take it — see the level remove button below.
  const addLevelRef = useRef<HTMLButtonElement | null>(null);

  /**
   * Brings the first refused control into view, and puts the caret in it.
   *
   * Scrolling alone reports the refusal to a sighted mouse user and to nobody
   * else: assistive tech announces what is focused, not what is on screen, and a
   * keyboard user left on Save still has to tab back through the whole panel to
   * reach the field. So the region is scrolled and the first focusable control
   * inside it is focused.
   *
   * Each ref holds a wrapper — a `TextField`'s `FormControl` root, the scope
   * section, a level card — so the focusable control is looked up inside it
   * rather than being the ref itself. The `[href]`-free query is deliberate:
   * these regions hold inputs and buttons, and the first of them in document
   * order is the one the message is about.
   *
   * Both DOM calls are guarded for the reason the mount effect above gives:
   * jsdom implements neither `scrollIntoView` nor layout, and a convenience for
   * sighted users must not be what makes the editor untestable.
   */
  const scrollToFirstError = (errors: ValidationErrors) => {
    const firstBadLevel = draft.levels.find(
      level => (errors.levels[level.key] ?? []).length > 0,
    );
    const levelTarget =
      (firstBadLevel && levelRefs.current[firstBadLevel.key]) || null;
    const target =
      (errors.name && nameFieldRef.current) ||
      (errors.scope && scopeSectionRef.current) ||
      levelTarget;

    target?.scrollIntoView?.({ block: 'start' });

    // A level card's only radios are its Any / All choice, which no level is
    // refused for and which sits above the roster — focus left there would turn
    // the next arrow key into an edit of the level's logic. So a level skips
    // radios; the scope section keeps them, because there the radios are the
    // refused control.
    const first = target?.querySelector?.<HTMLElement>(
      target === levelTarget
        ? 'input:not([type="hidden"]):not([type="radio"]), select, textarea, button:not([role="radio"])'
        : 'input:not([type="hidden"]), select, textarea, [role="radio"], button',
    );
    // A radio group is entered on its *checked* option, which is what tabbing
    // into one does natively — and the first radio in the scope group is "All
    // …", the choice the admin did not make. Landing there would announce the
    // wrong option as the one in error.
    const checkedInGroup =
      first instanceof HTMLInputElement &&
      first.type === 'radio' &&
      !first.checked
        ? target?.querySelector?.<HTMLElement>(
            `input[type="radio"][name="${first.name}"]:checked`,
          )
        : null;

    (checkedInGroup ?? first)?.focus?.();
  };

  const errors = validate(draft, fieldCatalog);
  const showErrors = submitted && hasErrors(errors);

  // Both the service and the options must keep a stable identity across renders.
  // Passed inline they are new objects every time, and the hook's internals
  // re-derive from them — which turns each keystroke in the name field into
  // another render, and the form tears itself down once React gives up. The
  // sibling `useApiService` documents the same requirement in as many words.
  // Annotated rather than inferred: create and update declare their responses
  // separately in the OpenAPI document, so the ternary infers a union of two
  // structurally identical types and the mutation hook — which resolves a single
  // `T` — cannot match it. The panel only ever reads `result.status`, so the
  // narrower of the two is the honest common type.
  const saveRule = useCallback(
    (payload: RulePayload): CancelablePromise<SaveRuleResponse> =>
      rule
        ? ApprovalsService.patchInternalApiApprovalsRules(
            rule.id as string,
            payload,
          )
        : ApprovalsService.postInternalApiApprovalsRules(payload),
    [rule],
  );
  const mutationOptions = useMemo(() => ({ onSuccess: onSaved }), [onSaved]);

  // Type arguments given rather than inferred: the hook's `T` only appears in the
  // service's return and in `onSuccess`, and this `onSuccess` takes no argument —
  // so there is nothing for TypeScript to infer `T` from and it lands on
  // `unknown`, which the annotated service then fails to satisfy.
  const { result, mutation } = useApiServiceMutation<
    SaveRuleResponse,
    typeof saveRule
  >(saveRule, mutationOptions);

  const approverLogicOptions: Array<{ value: ApproverLogic; label: string }> = [
    { value: 'any', label: intl.formatMessage(messages.approverLogicAny) },
    { value: 'all', label: intl.formatMessage(messages.approverLogicAll) },
  ];

  const updateLevel = (key: string, patch: Partial<LevelDraft>) =>
    setDraft(current => ({
      ...current,
      levels: current.levels.map(level =>
        level.key === key ? { ...level, ...patch } : level,
      ),
    }));

  /**
   * Moves a level to where it was dropped.
   *
   * Level order is the approval sequence — level 1 signs off before level 2 gets
   * asked — so this is an edit to the rule, not a rearrangement of a view of it.
   * See `reorderLevels` for why moving the array entry is the entire change.
   */
  const handleDragEnd = ({ source, destination }: DropResult) => {
    // No destination means the level was dropped outside the list, which reads
    // as a cancel rather than as a move to the end.
    if (!destination || destination.index === source.index) return;

    setDraft(current => ({
      ...current,
      levels: reorderLevels(current.levels, source.index, destination.index),
    }));
  };

  const handleSave = () => {
    setSubmitted(true);

    const refused = validate(draft, fieldCatalog);
    if (hasErrors(refused)) {
      scrollToFirstError(refused);
      return;
    }

    mutation(toPayload(draft, fieldCatalog));
  };

  /**
   * What the entity-naming copy selects on.
   *
   * A rule's subject type is fixed at creation, so this is constant for the life
   * of the panel — but it is read off the draft rather than the `subjectType`
   * prop, which is only passed when creating.
   */
  const subjectValues = { subject: draft.subjectType };

  /**
   * Whether the rule is a sequence, which is what a level card's chrome is for.
   *
   * A rule with one level has no order to state, nothing to reorder against and
   * nothing to remove itself from, and its scope is the panel's own "Apply this
   * rule to" — so the card drops the header band, the number, the name and the
   * conditions and shows only who signs off. Adding a second level brings all of
   * it back to both cards.
   */
  const multiLevel = draft.levels.length > 1;

  /**
   * The two exceptions, and why they read off the live draft rather than off the
   * stored rule.
   *
   * A one-level card still shows a field that holds a value, because `toPayload`
   * sends a level's name and its conditions whether or not this panel drew them
   * — so a hidden field would be data the admin can neither see nor delete. That
   * has to hold for a value typed a moment ago on a card that was one of two,
   * not only for one the rule was loaded with: removing the other level is
   * exactly how a name gets stranded. The live draft is the only source that
   * covers both.
   *
   * The mock hides them unconditionally and drops the data with them. That is a
   * bug in the mock, not a decision to transcribe.
   */
  const showLevelChrome = (level: LevelDraft) =>
    multiLevel || level.name !== '';
  // `conditions`, not `conditionPassthrough`, and that is a decision rather than
  // an oversight.
  //
  // `always` is the only type a level can hold that this build cannot author,
  // and on a level it is inert: `ConditionEvaluator#level_outcome` returns
  // `:applies` for an empty set, and `ALWAYS` evaluates to `:match`, so a level
  // whose only row is `always` gates exactly as much as a level with no rows at
  // all — which is what `AUTHORABLE_TYPES` says in its own words. Rendering the
  // section for it would announce a gate that is not there.
  //
  // The row still round-trips untouched; see `toStoredConditions`. Nothing is
  // lost by staying quiet about a value that changes no outcome.
  const showLevelConditions = (level: LevelDraft) =>
    multiLevel || level.conditions.length > 0;

  // Editing names the entity now too (AXHE-4467): one rule table backs both
  // filters, so "Edit rule" left the panel unable to say which of the two an
  // admin had opened.
  const title = rule ? messages.editRuleTitle : messages.createRuleTitle;

  // The scope rows the panel is not showing. Non-empty means the rule is scoped
  // by something it cannot render, which is what withholds "All …".
  const hiddenScope = hiddenScopeConditions(draft, fieldCatalog);
  // Why they are hidden decides which note explains it. With no catalog the
  // hidden rows are the rule's own conditions, kept off screen only because the
  // builder is showing its error state instead of them — recoverable by the Retry
  // it offers. With a catalog they are types this build does not understand at
  // all, which since AXHE-4383 means a type written by a newer server: every type
  // the design defines is rendered here now. Blaming the kind in the first case
  // would name a cause the admin can see is false one element down.
  const hiddenScopeNote = fieldCatalog
    ? messages.scopeConditionsElsewhere
    : messages.scopeConditionsUnavailable;
  const scopeIsAll = draft.scopeChoice === 'all';
  // Only worth saying with a sequence to describe: a one-level rule has no next
  // level to move to. And only on edit — `createRuleIntro` is a paraphrase of
  // this sentence sitting a few lines above, so on create the note would print
  // the same idea twice on one screen.
  const showScopeAllNote = scopeIsAll && multiLevel && Boolean(rule);
  const scopeNeedsCondition = showErrors && errors.scope === 'no_conditions';
  // A stored "matches everything" row sitting beside a narrowing one, on the
  // conditional choice: saving drops it. Under OR that row is what opens the
  // gate today, so the drop narrows the rule and the note says so; under AND it
  // is redundant and the note only reports the tidy-up. Named off the logic
  // rather than the panel's choice, which is already `conditions` here.
  const showScopeAlwaysDrop = scopeDropsAlways(draft, fieldCatalog);
  const scopeAlwaysDropNote =
    draft.scopeConditionLogic === 'or'
      ? messages.scopeAlwaysDroppedNarrows
      : messages.scopeAlwaysDropped;
  const scopeDescribedBy =
    [
      showScopeAllNote || scopeNeedsCondition ? SCOPE_HELPER_ID : undefined,
      hiddenScope.length > 0 ? SCOPE_HIDDEN_ID : undefined,
      showScopeAlwaysDrop ? SCOPE_ALWAYS_DROP_ID : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <>
      <div
        className={styles.panel}
        role="dialog"
        aria-label={intl.formatMessage(title, subjectValues)}
      >
        <div className={styles.panelHeader}>
          {/* h3, not h5: this theme's h5 is 12px, which would leave the panel's
              own title smaller than every line of copy beneath it. h3 is what the
              other recruiter-ui panels and modals use for the same job. */}
          <Typography variant="h3">
            <FormattedMessage {...title} values={subjectValues} />
          </Typography>

          {/* Esc and the backdrop already close the drawer, and both are
              invisible. The x is the affordance that says the panel is
              dismissable — named, not "close-button", because it is the one
              control here a screen-reader user reaches before any copy. */}
          <IconButton
            aria-label={intl.formatMessage(globalMessages.close)}
            className={styles.panelClose}
            onClick={onClose}
            size="small"
          >
            <Icon icon={XOutlineIcon} size="lg" color="inherit" />
          </IconButton>
        </div>

        <div className={styles.panelBody}>
          {/* One slot, two jobs (Design Requirements §3). An empty panel needs
              the level model explained before the admin builds one; an existing
              rule's own fields already say what it holds, and what they do not
              say is how far an edit here reaches. Neither sentence is the
              other's mode-flipped wording, so they are two messages. */}
          <Typography
            variant="body2"
            color="textSecondary"
            className={styles.panelIntro}
          >
            <FormattedMessage
              {...(rule ? messages.editRuleIntro : messages.createRuleIntro)}
              values={subjectValues}
            />
          </Typography>

          {result.status === 'error' && (
            <div className={styles.error} role="alert">
              <Typography variant="body1">
                <FormattedMessage {...messages.saveFailed} />
              </Typography>
            </div>
          )}

          {/* The label sits above the box rather than floating inside it, which
              is what every field on this panel does — the design system's own
              picker draws its label the same way, and `styles.fieldLabel` is
              that one style. MUI's `label` prop would put it back inside the
              control.

              `required` stays on the input: it is the half of the marking that
              reaches a screen reader, and it announces the field as mandatory
              before the save is refused rather than after. The asterisk is the
              visual half and is `aria-hidden`, so the two do not double up.
              There is no <form> around this panel, so `required` brings no
              native validation bubble to compete with the message below.

              Red, as Design Requirements §3 asks (AXHE-4468). Spending the error
              colour on a resting state is the trade `constraint-anti-pattern-guard`'s
              colour table cautions against, and it is bought back below: an
              invalid field takes a red *border* and a red ring, which is a change
              the asterisk alone cannot be mistaken for. */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="approval-rule-name">
              <FormattedMessage {...messages.ruleNameLabel} />
              <span aria-hidden className={styles.requiredMark}>
                {' *'}
              </span>
            </label>
            <TextField
              id="approval-rule-name"
              ref={nameFieldRef}
              className={styles.outlinedField}
              InputProps={{ disableUnderline: true }}
              placeholder={intl.formatMessage(messages.ruleNamePlaceholder)}
              required
              value={draft.name}
              // Reads the value out of the event before the updater, so the
              // updater closes over a string rather than the event. Every other
              // handler in this file reads `event.target` eagerly too.
              onChange={event => {
                const { value } = event.target;
                setDraft(current => ({ ...current, name: value }));
              }}
              error={showErrors && errors.name === 'required'}
              helperText={
                showErrors && errors.name === 'required'
                  ? intl.formatMessage(messages.ruleNameRequired)
                  : undefined
              }
              fullWidth
            />
          </div>

          <Divider className={styles.panelDivider} />

          {/* Directly under the name, as the mock has it, and before the levels
              for a reason: this is the question of which offers the rule touches
              at all, and the levels below only describe who signs off once it
              does. Reading them in the other order asks the admin to hold a
              sequence of approvers in mind before knowing what triggers it. */}
          {/* Named, because the panel renders one condition builder per level as
              well as this one: "Add condition" and a condition row are ambiguous
              across the whole panel, so a test that means the rule's scope has to
              say so. */}
          <div
            className={styles.scopeSection}
            data-testid="rule-scope"
            ref={scopeSectionRef}
          >
            <FormControl component="fieldset" fullWidth>
              {/* A legend, because a radio group has no single control to hang a
                  label on. Styled with `fieldLabel`, the same 14px/16px gray600
                  with a 4px gutter that "Approvers" and "Backup approvers"
                  already render at — one more field label on the panel, not a
                  section heading.

                  `focused={false}` overrides what `FormLabel` would otherwise
                  take from the surrounding `FormControl`: by default it turns
                  primary-coloured while any radio in the group has focus, which
                  fits a label tied to one input, not a legend naming the whole
                  group.

                  It carries an id and the group points at it. The fieldset/legend
                  pair is *not* enough on its own: MUI renders `RadioGroup` as a
                  `div role="radiogroup"` inside the fieldset, and it is that div
                  the assistive layer treats as the group — the legend names the
                  fieldset around it, so without the reference below the group has
                  no accessible name at all. */}
              <FormLabel
                component="legend"
                className={styles.fieldLabel}
                focused={false}
                id={SCOPE_LEGEND_ID}
              >
                <FormattedMessage {...messages.scopeLegend} />
              </FormLabel>

              <RadioGroup
                // `name` is what scopes native radio traversal, so the pair is one
                // tab stop and the arrow keys move between the choices. MUI
                // generates one when it is omitted; naming it keeps it stable
                // across mounts and legible in a DOM dump.
                name="approval-rule-scope"
                // One line, as the design has it — see `scopeRadios`.
                row
                className={styles.scopeRadios}
                value={draft.scopeChoice}
                aria-labelledby={SCOPE_LEGEND_ID}
                // Described on the group rather than on each radio, so the note
                // under it is announced once on entry instead of again on every
                // arrow press.
                aria-describedby={scopeDescribedBy}
                // Marks the group invalid to assistive tech once the save is
                // refused for it, the counterpart of the `error` prop MUI puts on
                // the name TextField. Without it a screen-reader user re-entering
                // the group after a refusal hears `scopeNeedsCondition` as a
                // neutral description rather than as the error it is — the one
                // gate-relevant validation message on the panel would be its least
                // discoverable.
                aria-invalid={scopeNeedsCondition || undefined}
                onChange={event => {
                  // Read eagerly, as the name field's handler does, so the updater
                  // closes over the value rather than the event.
                  const { value } = event.target;
                  setDraft(current =>
                    withScopeChoice(
                      current,
                      value as RuleDraft['scopeChoice'],
                      fieldCatalog,
                    ),
                  );
                }}
              >
                {/* Neither carries `checked`: the group's `value` decides which is
                    selected, and MUI's Radio derives it from `RadioGroupContext`
                    when the prop is absent. Passing both left one piece of state
                    with two owners that happened to agree.

                    Withheld, not hidden: the "All …" choice exists and is
                    unavailable for a reason the note below states. Hiding it would
                    leave a radio group of one, which reads as a control that does
                    nothing. */}
                <Radio
                  value="all"
                  disabled={hiddenScope.length > 0}
                  label={intl.formatMessage(messages.scopeAll, subjectValues)}
                />
                <Radio
                  value="conditions"
                  label={intl.formatMessage(
                    messages.scopeConditional,
                    subjectValues,
                  )}
                />
              </RadioGroup>

              {/* The first two share one id because they can never both be due:
                  the validation message belongs to the conditional choice and the
                  pass-through explanation to the other. The note below them is a
                  property of the rule rather than of the choice, so it has an id
                  of its own and both can be referenced at once. */}
              {scopeNeedsCondition && (
                // `role="alert"` because it is mounted only when the save is
                // refused, so its appearance is the event to announce — the group
                // itself is only re-read on focus, which a refused save does not
                // move.
                <FormHelperText id={SCOPE_HELPER_ID} error role="alert">
                  <FormattedMessage
                    {...messages.scopeNeedsCondition}
                    values={subjectValues}
                  />
                </FormHelperText>
              )}
              {showScopeAllNote && (
                <FormHelperText
                  id={SCOPE_HELPER_ID}
                  className={styles.scopeAllNote}
                >
                  <FormattedMessage
                    {...messages.scopeAllLevels}
                    values={subjectValues}
                  />
                </FormHelperText>
              )}
              {hiddenScope.length > 0 && (
                <FormHelperText id={SCOPE_HIDDEN_ID}>
                  <FormattedMessage
                    {...hiddenScopeNote}
                    values={subjectValues}
                  />
                </FormHelperText>
              )}
              {/* The "matches everything" row that a save will drop. `role="status"`
                  rather than `alert`: it is not a validation failure and does not
                  block the save, it is a heads-up about a change the save makes to
                  the stored data. It is mounted only while that data is present, so
                  its appearance is worth announcing without stealing focus. */}
              {showScopeAlwaysDrop && (
                <FormHelperText id={SCOPE_ALWAYS_DROP_ID} role="status">
                  <FormattedMessage
                    {...scopeAlwaysDropNote}
                    values={subjectValues}
                  />
                </FormHelperText>
              )}
            </FormControl>

            {/* Outside the FormControl above, deliberately. MUI's FormControl
                pushes `error`, `disabled` and `margin` down to every input inside
                it through context, which would let the scope group's own state
                overwrite what each condition row is saying about itself. */}
            {!scopeIsAll && (
              <ConditionBuilder
                conditions={draft.scopeConditions}
                catalog={catalog}
                onRetryCatalog={onRetryCatalog}
                showErrors={showErrors}
                idPrefix={SCOPE_CONDITION_PREFIX}
                showHeading={false}
                subjectType={draft.subjectType}
                // A rule's scope, not a level: the server refuses a pay condition
                // and an unsourced attribute here, because an unevaluatable scope
                // condition drops the whole rule instead of holding it.
                scope="scope"
                onChange={scopeConditions =>
                  setDraft(current => ({ ...current, scopeConditions }))
                }
                logic={draft.scopeConditionLogic}
                onLogicChange={scopeConditionLogic =>
                  setDraft(current => ({ ...current, scopeConditionLogic }))
                }
              />
            )}
          </div>

          <Divider className={styles.panelDivider} />

          {/* A level's number is its place in the sequence, so the heading is
              derived from the array index rather than stored — which is what makes
              a reorder renumber the cards it moved without touching the drafts. */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="approval-rule-levels">
              {(droppable, listSnapshot) => (
                <div
                  ref={droppable.innerRef}
                  {...droppable.droppableProps}
                  className={clsx(
                    styles.levelList,
                    listSnapshot.isDraggingOver && styles.levelListDropTarget,
                  )}
                >
                  {draft.levels.map((level, index) => (
                    <Draggable
                      key={level.key}
                      draggableId={level.key}
                      index={index}
                      // A one-level rule has nothing to reorder. This is also what
                      // withholds the handle: a disabled draggable is handed a null
                      // `dragHandleProps`, so the control exists exactly when
                      // using it would do something.
                      isDragDisabled={!multiLevel}
                    >
                      {(draggable, snapshot) => (
                        <div
                          // Three refs on one element: the drag library owns the
                          // node and measures it, so its callback has to keep
                          // being called — the focus ref borrows the same node to
                          // scroll to it once on open, and the error map borrows
                          // it to scroll to a refused level.
                          ref={element => {
                            draggable.innerRef(element);
                            levelRefs.current[level.key] = element;
                            if (index + 1 === focusLevel) {
                              focusedLevelRef.current = element;
                            }
                          }}
                          {...draggable.draggableProps}
                          className={clsx(
                            styles.levelCard,
                            snapshot.isDragging && styles.levelCardDragging,
                          )}
                          data-testid="level-card"
                        >
                          {/* The band, and everything in it, is what a rule with
                              one level does without: there is no sequence to
                              number, nothing to reorder it against, and nothing
                              to remove it from. The exception is a name the level
                              already carries — hiding a field that holds a value
                              the save still sends is the one thing the
                              simplification must not do, so the band renders for
                              it. A name cleared to empty takes the band with it,
                              which is correct: a one-level rule is not offered a
                              level name, so there is nothing to type back into. */}
                          {showLevelChrome(level) && (
                            <div className={styles.levelCardHeader}>
                              {draggable.dragHandleProps && (
                                <span
                                  {...draggable.dragHandleProps}
                                  className={styles.dragHandle}
                                  aria-label={intl.formatMessage(
                                    messages.reorderLevel,
                                    { number: index + 1 },
                                  )}
                                >
                                  <Icon
                                    icon={DragAndDropOutlineIcon}
                                    size="xs"
                                    color="inherit"
                                  />
                                </span>
                              )}

                              {multiLevel && (
                                <Typography
                                  variant="h4"
                                  component="span"
                                  className={styles.levelNumber}
                                >
                                  <FormattedMessage
                                    {...messages.levelHeading}
                                    values={{ number: index + 1 }}
                                  />
                                </Typography>
                              )}

                              {/* Inline in the band rather than a labelled field
                                  below it, so the band reads as the card's title
                                  row. The visible label goes with it, so the name
                                  is carried by `aria-label` — the placeholder is
                                  not a label, and a control whose only name is
                                  placeholder text has none once it holds a
                                  value. */}
                              <TextField
                                id={`level-name-${level.key}`}
                                className={clsx(
                                  styles.outlinedField,
                                  styles.levelNameField,
                                )}
                                InputProps={{ disableUnderline: true }}
                                inputProps={{
                                  'aria-label': intl.formatMessage(
                                    messages.levelNameLabel,
                                  ),
                                }}
                                placeholder={intl.formatMessage(
                                  messages.levelNamePlaceholder,
                                )}
                                value={level.name}
                                onChange={event =>
                                  updateLevel(level.key, {
                                    name: event.target.value,
                                  })
                                }
                              />

                              {/* Destructive, so it is never primary weight — a text
                                  button at the end of the header row, not a filled
                                  button next to Save.

                                  An icon, not a `×` text node, for the reason the
                                  condition builder's remove already gives: a glyph
                                  has no icon box, so the button measured 17px square
                                  — under the minimum pointer target. */}
                              {multiLevel && (
                                <IconButton
                                  size="small"
                                  className={styles.levelRemove}
                                  aria-label={intl.formatMessage(
                                    messages.removeLevel,
                                  )}
                                  onClick={() => {
                                    // Read before the state update unmounts
                                    // this card — `levelRefs` is keyed by the
                                    // level, not the DOM, so the neighbour is
                                    // still there to look up by its own key.
                                    const next = draft.levels[index + 1];
                                    const prev = draft.levels[index - 1];
                                    focusAfterRemoval(
                                      [
                                        next && levelRefs.current[next.key],
                                        prev && levelRefs.current[prev.key],
                                      ],
                                      () => addLevelRef.current,
                                    );
                                    setDraft(current => ({
                                      ...current,
                                      levels: current.levels.filter(
                                        l => l.key !== level.key,
                                      ),
                                    }));
                                  }}
                                >
                                  <Icon
                                    icon={XOutlineIcon}
                                    size="sm"
                                    color="inherit"
                                  />
                                </IconButton>
                              )}
                            </div>
                          )}

                          {/* Above the approvers, and first in the card: the level
                              reads "does this apply, and then who signs off".
                              Withheld from a one-level rule, whose scope is the
                              rule's own "Apply this rule to" — with one gate on
                              the card and another on the panel, the two read as
                              alternatives rather than as a rule and a level.

                              Kept when the level already holds conditions, for the
                              reason the band's name field is kept: they survive
                              the save either way, so hiding them would only make
                              them unremovable. Removing the last one collapses the
                              section, which is the same rule the band follows. */}
                          {showLevelConditions(level) && (
                            <div
                              className={clsx(
                                styles.levelSection,
                                styles.levelSectionDivided,
                              )}
                            >
                              <ConditionBuilder
                                conditions={level.conditions}
                                catalog={catalog}
                                onRetryCatalog={onRetryCatalog}
                                showErrors={showErrors}
                                idPrefix={level.key}
                                subjectType={draft.subjectType}
                                scope="level"
                                onChange={conditions =>
                                  updateLevel(level.key, { conditions })
                                }
                                logic={level.conditionLogic}
                                onLogicChange={conditionLogic =>
                                  updateLevel(level.key, { conditionLogic })
                                }
                              />
                            </div>
                          )}

                          <div className={styles.levelSection}>
                            {/* The section's caption, drawn here rather than by
                                the picker so the Any / All segments can share
                                its row. The picker keeps "Approvers" as its
                                accessible name. */}
                            <div className={styles.approversCaptionRow}>
                              <Typography
                                variant="h5"
                                component="span"
                                className={styles.sectionCaption}
                              >
                                <FormattedMessage
                                  {...messages.approversLabel}
                                />
                              </Typography>

                              {/* From two approvers upwards, as the design has it:
                                  with one, "any of them" and "all of them" name the
                                  same person, so a control offering both is one
                                  that does nothing.

                                  Counted over named users *and* role approvers,
                                  which the picker cannot show but the runtime
                                  resolves — a level staffed by one user and two
                                  roles has three approvers and the choice is live.

                                  Unlabeled on screen, as the design draws it: the
                                  caption is what the segments qualify. The group
                                  still needs a name of its own — see
                                  `approverLogicLabel`. */}
                              {approverCount(level) > 1 && (
                                <SegmentedChoice
                                  dense
                                  className={styles.approverLogicChoice}
                                  label={intl.formatMessage(
                                    messages.approverLogicLabel,
                                  )}
                                  onChange={approverLogic =>
                                    updateLevel(level.key, { approverLogic })
                                  }
                                  options={approverLogicOptions}
                                  value={level.approverLogic}
                                />
                              )}
                            </div>

                            {/* Design Requirements §3 asks for a searchable
                                person picker here — the same control the
                                condition rows take, in flat mode, because an
                                account names hundreds of people and a roster
                                that can only be scrolled is unusable at that
                                size. The message is passed as a string rather
                                than as MUI helper text so the picker renders it
                                against its own control and wires
                                `aria-describedby` to the real input. */}
                            <SearchableMultiSelect
                              id={`level-approvers-${level.key}`}
                              label={intl.formatMessage(
                                messages.approversLabel,
                              )}
                              hideLabel
                              values={level.approverIds}
                              options={approverOptions}
                              onChange={approverIds =>
                                updateLevel(level.key, { approverIds })
                              }
                              error={
                                showErrors &&
                                levelHasError(errors, level.key, 'no_approvers')
                                  ? intl.formatMessage(
                                      messages.approverRequired,
                                    )
                                  : undefined
                              }
                            />

                            {/*
                              The level lists approvers whose users have been
                              offboarded. They cannot be sent back (the write
                              contract needs a user_id the read API no longer has),
                              so saving drops them — which matches the engine, where
                              an offboarded approver already resolves to nobody.
                              Saying so is the point: the same edit used to shrink
                              the level in silence.
                            */}
                            {level.offboardedApproverCount > 0 && (
                              <Typography
                                variant="body2"
                                className={styles.inlineWarning}
                                role="status"
                              >
                                <FormattedMessage
                                  {...messages.offboardedApprovers}
                                  values={{
                                    count: level.offboardedApproverCount,
                                  }}
                                />
                              </Typography>
                            )}

                            {/* Directly under the roster it stands in for, and last
                                in the approver section: a backup is the same
                                question as an approver — who signs off — asked about
                                the case where nobody does (AXHE-3932). The offboarded
                                warning sits between the two because it counts rows
                                from both. */}
                            <BackupApprovers
                              backup={level.backup}
                              approverOptions={approverOptions}
                              idPrefix={level.key}
                              timerError={
                                (showErrors &&
                                  ((levelHasError(
                                    errors,
                                    level.key,
                                    'bad_escalation_timer',
                                  ) &&
                                    'invalid') ||
                                    (levelHasError(
                                      errors,
                                      level.key,
                                      'no_escalation_timer',
                                    ) &&
                                      'missing') ||
                                    (levelHasError(
                                      errors,
                                      level.key,
                                      'no_escalation_unit',
                                    ) &&
                                      'unit'))) ||
                                undefined
                              }
                              rosterError={
                                showErrors &&
                                levelHasError(
                                  errors,
                                  level.key,
                                  'no_backup_approvers',
                                )
                              }
                              onChange={backup =>
                                updateLevel(level.key, { backup })
                              }
                            />

                            {/*
                              Only ever reached by a level switched off before
                              AXHE-4470 removed the switch. Said out loud rather than
                              left as an invisible property: the level lists
                              approvers, sits in the sequence, and is skipped anyway,
                              so an admin reading this card has no other way to tell
                              why the rule never stops there. Read-only — this panel
                              no longer owns the value, it only declines to
                              overwrite it.
                            */}
                            {!level.enabled && (
                              <Typography
                                variant="body2"
                                className={styles.inlineWarning}
                                role="status"
                              >
                                <FormattedMessage {...messages.levelOff} />
                              </Typography>
                            )}
                          </div>

                          {/* Decorative, so it is hidden: the numbering on the
                              cards already says they are a sequence, and the arrow
                              only says it faster. Never after the last card —
                              there is no level after it to point at. */}
                          {index < draft.levels.length - 1 && (
                            <span
                              className={styles.levelArrow}
                              aria-hidden
                              data-testid="level-arrow"
                            />
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {droppable.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Button
            ref={addLevelRef}
            className={clsx(styles.addLink, styles.addLevel)}
            startIcon={
              <Icon icon={PlusOutlineIcon} size="xs" color="inherit" />
            }
            onClick={() => {
              // Minted outside the updater: bumping the ref inside it would make
              // the updater impure, and React may call it more than once.
              const key = mintLevelKey();
              setDraft(current => ({
                ...current,
                levels: [...current.levels, emptyLevel(key)],
              }));
            }}
          >
            <FormattedMessage {...messages.addLevel} />
          </Button>
        </div>

        <div className={styles.panelActions}>
          <Button
            className={styles.panelButton}
            variant="outlined"
            onClick={onClose}
          >
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          {/* Two labels, not one "Save rule": creating a rule and saving an edit
              are different actions, and this is the last thing read before
              either commits. */}
          <Button
            className={styles.panelButton}
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={result.status === 'loading'}
          >
            <FormattedMessage
              {...(rule ? messages.saveRuleChanges : messages.createRuleSubmit)}
            />
          </Button>
        </div>
      </div>
    </>
  );
};

/**
 * The form in its slide-in container.
 *
 * Split from `RuleEditorForm` so the form can be exercised on its own. MUI v4's
 * Drawer renders through a Modal portal, and under jsdom repeated re-renders of
 * the portal's contents detach it from the document — so a test that types more
 * than a character or two into a field loses the whole subtree and every
 * subsequent query fails, for reasons that have nothing to do with the form.
 *
 * The split is worth having regardless: the form owns the draft, the validation
 * and the write, and the drawer is only where it sits.
 */
export const RuleEditorPanel: React.FC<RuleEditorPanelProps> = ({
  open,
  onClose,
  ...formProps
}) => {
  const styles = useStyles();

  return (
    // The paper stops scrolling so the form's own body can — see `panelPaper`.
    <Drawer
      anchor="right"
      classes={{ paper: styles.panelPaper }}
      onClose={onClose}
      open={open}
    >
      <RuleEditorForm onClose={onClose} {...formProps} />
    </Drawer>
  );
};
